import React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 16, color: '#f9fafb' }}>{title}</h2>
    <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '0.95rem' }}>{children}</div>
  </div>
);

const TermsPage: React.FC = () => {
  return (
    <main style={{ paddingTop: 100 }}>
      <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 80px' }}>
        <div style={{ marginBottom: 48 }}>
          <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900, marginBottom: 12 }}>Terms of Service</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Last updated: January 1, 2025</p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>By accessing or using PRETTIFAI ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. These Terms constitute a legally binding agreement between you ("User") and PRETTIFAI Ltd., a company incorporated under the laws of the State of Israel.</p>
        </Section>

        <Section title="2. Description of Service">
          <p>PRETTIFAI is a SaaS platform that connects to your Gmail account via OAuth2 authorization to automatically detect, extract, and organize invoice and receipt data. The Service uses artificial intelligence to process email attachments and content for the purpose of financial document management.</p>
        </Section>

        <Section title="3. Account Registration">
          <p style={{ marginBottom: 12 }}>To use the Service, you must:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Be at least 18 years of age or the age of majority in your jurisdiction</li>
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Accept responsibility for all activities that occur under your account</li>
          </ul>
          <p style={{ marginTop: 12 }}>You may not share your account or allow unauthorized third parties to access the Service through your credentials.</p>
        </Section>

        <Section title="4. Credit Packs and Billing">
          <p style={{ marginBottom: 12 }}>PRETTIFAI operates on a pay-as-you-go model. There are no subscriptions or recurring charges. You purchase credit packs — one-time payments that give you a fixed number of documents to process. Credits never expire.</p>
          <p style={{ marginBottom: 12 }}>Billing is handled by Paddle.com, our authorized reseller and Merchant of Record. By purchasing, you agree to Paddle's terms and conditions in addition to these Terms.</p>
          <p style={{ marginBottom: 12 }}>You may optionally enable Auto Top-Up, which automatically purchases a selected credit pack when your balance drops below a threshold you set. Auto Top-Up can be disabled at any time from your account settings.</p>
          <p>Prices are listed in USD and may be subject to applicable taxes depending on your jurisdiction, as determined by Paddle at checkout.</p>
        </Section>

        <Section title="5. Free Trial">
          <p>New accounts receive a free trial that includes a limited number of documents to process at no cost. No credit card is required to start the trial. PRETTIFAI reserves the right to modify or discontinue the free trial at any time with reasonable notice.</p>
        </Section>

        <Section title="6. Acceptable Use Policy">
          <p style={{ marginBottom: 12 }}>You agree not to use the Service to:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Violate any applicable law, regulation, or third-party rights</li>
            <li>Process invoices or data belonging to third parties without their authorization</li>
            <li>Attempt to reverse-engineer, decompile, or gain unauthorized access to any portion of the Service</li>
            <li>Transmit malicious code, spam, or interfere with the Service's operation</li>
            <li>Use the Service for any fraudulent or illegal purpose</li>
          </ul>
        </Section>

        <Section title="7. Data and Privacy">
          <p>Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to the collection, processing, and use of your data as described in the Privacy Policy. We access your Gmail data solely to provide the invoice extraction functionality you have requested.</p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>All intellectual property rights in the Service, including but not limited to software, algorithms, designs, and content, are owned by or licensed to PRETTIFAI Ltd. You are granted a limited, non-exclusive, non-transferable license to use the Service in accordance with these Terms. Your invoice data remains your property.</p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p style={{ marginBottom: 12 }}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PRETTIFAI AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR:</p>
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Any indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, data, or business opportunities</li>
            <li>Inaccuracies in AI-extracted invoice data</li>
            <li>Service interruptions or unavailability</li>
          </ul>
          <p style={{ marginTop: 12 }}>IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT PAID BY YOU TO PRETTIFAI IN THE 12 MONTHS PRECEDING THE CLAIM.</p>
        </Section>

        <Section title="10. Disclaimer of Warranties">
          <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. PRETTIFAI DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI EXTRACTIONS WILL BE 100% ACCURATE. YOU ARE RESPONSIBLE FOR VERIFYING EXTRACTED DATA BEFORE USING IT FOR ACCOUNTING OR LEGAL PURPOSES.</p>
        </Section>

        <Section title="11. Termination">
          <p>You may terminate your account at any time through the account settings. PRETTIFAI may suspend or terminate your access immediately if you breach these Terms or if required to do so by law. Upon termination, your data will be retained for 30 days, after which it will be permanently deleted unless a longer retention period is required by law.</p>
        </Section>

        <Section title="12. Modifications to Terms">
          <p>PRETTIFAI reserves the right to modify these Terms at any time. We will notify you of material changes via email or in-app notification at least 14 days before the changes take effect. Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.</p>
        </Section>

        <Section title="13. Governing Law and Dispute Resolution">
          <p>These Terms are governed by and construed in accordance with the laws of the State of Israel, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved exclusively in the competent courts of Tel Aviv, Israel. You waive any objection to the personal jurisdiction of such courts.</p>
        </Section>

        <Section title="14. Contact Information">
          <p>For questions about these Terms, please contact us at:</p>
          <p style={{ marginTop: 8 }}>
            <strong style={{ color: '#f9fafb' }}>PRETTIFAI Ltd.</strong><br />
            Contact: <a href="/contact" style={{ color: "#60a5fa" }}>Contact Form</a>
          </p>
        </Section>
      </div>
    </main>
  );
};

export default TermsPage;
